import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class StockBatch extends ModelObject<StockBatch> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _dateOfBatch: LocalDate | null = null;
    /**
    * Date of Batch for batchable stock. Typically date the batch (urn) was first received. Used for FIFO.
    */
    public get dateOfBatch(): LocalDate | null { return this._dateOfBatch }
    public set dateOfBatch(value: LocalDate | null) {
      this._dateOfBatch = value === undefined ? null : value;
    }
    /**
    * Stock Item Id
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * URN
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    /**
    * Is batch complete
    */
    public isComplete: boolean = false;
    private _hopAlpha: number | null = null;
    /**
    * Hop Alpha value of this batch, if specified.
    * Not Negative
    */
    public get hopAlpha(): number | null { return this._hopAlpha }
    public set hopAlpha(value: number | null) {
      this._hopAlpha = value === undefined ? null : value;
    }
    private _bestBeforeDate: LocalDate | null = null;
    /**
    * Best before date for batchable stock. Used for product batch tracking.
    */
    public get bestBeforeDate(): LocalDate | null { return this._bestBeforeDate }
    public set bestBeforeDate(value: LocalDate | null) {
      this._bestBeforeDate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.dateOfBatch = jsonSerializedObject.dateOfBatch == null ? null : LocalDate.parse(jsonSerializedObject.dateOfBatch);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.urn = jsonSerializedObject.urn;
          this.isComplete = jsonSerializedObject.isComplete;
          this.hopAlpha = jsonSerializedObject.hopAlpha == null ? null : jsonSerializedObject.hopAlpha;
          this.bestBeforeDate = jsonSerializedObject.bestBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.bestBeforeDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.dateOfBatch = this.dateOfBatch == null ? null : this.dateOfBatch.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.urn = this.urn;
        toRet.isComplete = this.isComplete;
        toRet.hopAlpha = this.hopAlpha == null ? null : this.hopAlpha;
        toRet.bestBeforeDate = this.bestBeforeDate == null ? null : this.bestBeforeDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockBatch.Id cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "StockBatch.StockItemId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
    ];
}


export class ItemUrn extends ModelObject<ItemUrn> {

    public itemId: Guid = Guid.createEmpty();
    public urn: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.urn = jsonSerializedObject.urn;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.itemId = this.itemId.toString();
        toRet.urn = this.urn;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "ItemUrn.ItemId cannot be empty"},
    ];
}


export class StockBatchFilter extends ModelObject<StockBatchFilter> {

    private _stockItemIds: readonly (Guid)[] | null = null;
    public get stockItemIds(): readonly (Guid)[] | null { return this._stockItemIds }
    public set stockItemIds(value: readonly (Guid)[] | null) {
      this._stockItemIds = value === undefined ? null : value;
    }
    private _fromDateOfBatch: LocalDate | null = null;
    public get fromDateOfBatch(): LocalDate | null { return this._fromDateOfBatch }
    public set fromDateOfBatch(value: LocalDate | null) {
      this._fromDateOfBatch = value === undefined ? null : value;
    }
    public isComplete: EnumYesNoEither = EnumYesNoEither.either;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemIds = jsonSerializedObject.stockItemIds == null ? null : jsonSerializedObject.stockItemIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromDateOfBatch = jsonSerializedObject.fromDateOfBatch == null ? null : LocalDate.parse(jsonSerializedObject.fromDateOfBatch);
          this.isComplete = EnumYesNoEither.getByValue(jsonSerializedObject.isComplete, this.isComplete);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemIds = this.stockItemIds == null ? null : this.stockItemIds.map(v => v.toString());
        toRet.fromDateOfBatch = this.fromDateOfBatch == null ? null : this.fromDateOfBatch.toString();
        toRet.isComplete = this.isComplete.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class StockBatchService extends ServiceBase {

  public async getStockBatchesForItems(stockItemIds: readonly Guid[], completeOrNot: EnumYesNoEither, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockItemIds:stockItemIds.map(v => v.toString()),
    completeOrNot:completeOrNot.value,
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getStockBatchesForItems", req, false, extractor, opts);
  };

  public async getStockBatches(stockBatchIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockBatchIds:stockBatchIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getStockBatches", req, false, extractor, opts);
  };

  public async saveStockBatch(stockBatch: StockBatch, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockBatch:stockBatch.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockBatch/v1/saveStockBatch", req, true, extractor, opts);
  };

  public async setStockBatchIsComplete(stockBatchId: Guid, complete: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockBatchId:stockBatchId.toString(),
    complete:complete,
    };
    const extractor = null;
    return this.makeJsonRequest("stockBatch/v1/setStockBatchIsComplete", req, true, extractor, opts);
  };

  public async setStockBatchesIsComplete(stockBatchIds: readonly Guid[], complete: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockBatchIds:stockBatchIds.map(v => v.toString()),
    complete:complete,
    };
    const extractor = null;
    return this.makeJsonRequest("stockBatch/v1/setStockBatchesIsComplete", req, true, extractor, opts);
  };

  public async getZeroNonCompletedStockBatches(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getZeroNonCompletedStockBatches", req, false, extractor, opts);
  };

  public async getStockBatchesForItemUrns(itemUrns: readonly ItemUrn[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    itemUrns:itemUrns.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getStockBatchesForItemUrns", req, false, extractor, opts);
  };

  public async getStockBatchesByFilter(filter: StockBatchFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getStockBatchesByFilter", req, false, extractor, opts);
  };
}

export const stockBatchService = new StockBatchService();